import memoize from "memoize-one"

import { isValidDate, parseDate, sentencify, stringifyDate } from "../utils"
import { isLTPM } from "../../components/shared_components_registration"

const registration = {
  extra_date_tags: isLTPM
    ? []
    : [
        {
          tag: "started",
          title: "Started On",
          category: "Run Parameters",
        },
      ],
  extra_option_tags: isLTPM
    ? [
        {
          tag: "project",
          title: "Project",
        },
        {
          tag: "task",
          title: "Task",
        },
        {
          tag: "netsuite_project",
          title: "Netsuite Project ID",
        },
        {
          tag: "experiment",
          title: "Experiment",
        },
      ]
    : [
        {
          tag: "site",
          title: "Site",
        },
        {
          tag: "owner",
          title: "Owner",
        },
        {
          tag: "netsuite_project",
          title: "Netsuite Project",
        },
        {
          tag: "event_labels",
          title: "Run Event Label",
        },
        {
          tag: "skynaught_event_labels",
          title: "[Skynaught] Run Event Label",
        },
        {
          tag: "samples",
          title: "Sample Type",
        },
      ],
}

export const DATE_TAGS = [
  ...registration.extra_date_tags,
  {
    tag: "inoculated",
    title: "Inoculated On",
    category: "Run Parameters",
  },
  {
    tag: "finished",
    title: "Finished On",
    category: "Run Parameters",
  },
].map((tag) => ({ ...tag, isDate: true }))

export const RUN_TAGS = [
  {
    tag: "run_length",
    title: "Run Length",
    category: "Run Parameters",
    units_options: ["Days"],
    isNumerical: true,
  },
]

export const OPTION_TAGS = [
  ...registration.extra_option_tags,
  {
    tag: "placeholder",
    title: "Placeholder",
  },
  {
    tag: "active",
    title: "Active",
  },
  {
    tag: "reactor",
    title: "Reactor",
  },
].map((tag) => ({ ...tag, options: [], category: "Run Parameters", isDropdown: true }))

export const getValidFilters = (structuredFilters) => {
  const filters = []
  for (const filter of structuredFilters) {
    if (
      filter.valueString &&
      (filter.isJsonField || filter.isDate || filter.isDropdown || filter.tag === "run_length")
    ) {
      const info = {
        tag: filter.tag,
        value: filter.valueString,
      }
      if (filter.isJsonField) {
        filters.push({
          ...info,
          isJsonField: filter.isJsonField,
          jsonBase: filter.jsonBase,
        })
      } else if (filter.isDate) {
        filters.push({
          ...info,
          isDate: filter.isDate,
          relation: filter.relation,
        })
      } else if (filter.isDropdown && filter.valueString !== "All" && filter.valueString !== "Not Required") {
        filters.push({
          ...info,
          isDropdown: filter.isDropdown,
        })
      } else if (filter.tag === "run_length") {
        filters.push({
          ...info,
          relation: filter.relation,
        })
      }
    } else if (filter.isTag) {
      // Handle filters for specific Observation tags - Purely 'an existence of any data for tag' lookup
      filters.push({
        tag: "tag",
        isTag: filter.isTag,
        value: filter.tag,
      })
    }
  }
  return filters
}

export const makeTagList = memoize(
  (tagInfo, activeFilters, options_map, TEXT_TAGS = [], SKIP_OPTIONS = []) => [
    ...DATE_TAGS.map((tag) => ({
      ...tag,
      notAvailable: !!activeFilters.includes(tag.tag),
    })),
    ...RUN_TAGS.map((tag) => ({
      ...tag,
      notAvailable: !!activeFilters.includes(tag.tag),
    })),
    ...TEXT_TAGS.map((mkey) => ({
      tag: mkey,
      title: sentencify(mkey),
      category: "Run Metadata",
      isText: true,
      isJsonField: true,
      jsonBase: "metadata",
      notAvailable: !!activeFilters.includes(mkey),
    })),
    ...OPTION_TAGS.filter((tag) => !SKIP_OPTIONS.includes(tag.tag)).map((tag) => ({
      ...tag,
      options: options_map?.[tag.tag] || [],
      notAvailable: !!activeFilters.includes(tag.tag),
    })),
    ...[].concat.apply(
      [],
      (tagInfo || [])
        .filter((tag) => tag.types[0])
        .map((tag) => {
          let tag_category_options = []
          for (const cat of tag.types) {
            if (cat !== "Site") {
              tag_category_options.push({
                ...tag,
                title: tag.label || tag.name,
                tag: tag.name,
                category: cat,
                type_options: tag.types,
                isTag: true,
              })
            }
          }
          return tag_category_options
        }),
    ),
  ],
)

export function handleRunFilters(filters) {
  const params = {}
  const tagsSelected = []
  for (const filter of filters || []) {
    if (filter.tag === "keyword") {
      params["full_text_search"] = decodeURI(filter.value)
    } else if (filter.isJsonField && filter.jsonBase) {
      // Currently only used on LL side
      params[`${filter.jsonBase}__${filter.tag}`] = filter.value
    } else if (filter.isDate) {
      const ts = parseDate(filter.value)
      if (!isValidDate(ts)) {
        throw Error(`Expecting a date, got ${filter.value}`)
      }
      params[`${filter.tag}__${filter.relation}`] = stringifyDate(ts, true, true)
    } else if (filter.isDropdown) {
      // No encoding here as it alters an expected value that uses a double-underscore
      params[`${filter.tag}__exact`] = filter.value
    } else if (filter.isTag) {
      tagsSelected.push(filter.value)
    } else if (filter.tag && filter.relation && filter.value) {
      params[`${filter.tag}__${filter.relation}`] = filter.value
    } else {
      throw Error(`Encountered an unexpected filter type: '${filter.tag}'`)
    }
  }

  if (tagsSelected.length > 0) {
    params["tags__exact"] = tagsSelected.join(",")
  }

  return params
}
